import { Fragment, useState } from 'react';
import { LocalizedPageTitle, LocalizedParagraph, OverlayedLoader } from '@mews-commander/ui';
import { getFullName, useGetAvatarSrc } from '@mews-commander/utils';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Accordion, Avatar, Button, Divider, Stack } from '@optimus-web/core';
import { useCustomDocumentTitle } from '../../../../../layouts/useCustomDocumentTitle';
import { NewPublicFormWrapper } from '../../style';
import { UnlockForm } from './UnlockForm/UnlockForm';
import { selectLockedUser, useLockedUsers, useSelectedLockedUser } from './useLockscreenStore';

interface UnlockScreenContentProps {
    onUserChange: () => void;
}

export const UnlockScreen = ({ onUserChange }: UnlockScreenContentProps) => {
    useCustomDocumentTitle({ textKey: TextKey.Unlock });

    const getAvatarSrc = useGetAvatarSrc();
    const users = useLockedUsers();
    const selectedUser = useSelectedLockedUser()?.user;
    const [isSaving, setIsSaving] = useState(false);

    const toggleSelectUser = (userId: string) => {
        const id = userId === selectedUser?.id ? null : userId;
        selectLockedUser(id);
    };

    const loginWithAnotherAccount = () => {
        selectLockedUser(null);
        onUserChange();
    };

    return (
        <NewPublicFormWrapper>
            <OverlayedLoader show={isSaving} />
            <Stack spacing="200" vertical fill>
                <Stack spacing="400" vertical>
                    <Stack spacing="100" vertical>
                        <LocalizedPageTitle textKey={TextKey.LockScreenTitle} />
                        <LocalizedParagraph textKey={TextKey.LockScreenInstructions} variant="secondary" />
                    </Stack>
                    <Stack vertical>
                        <Divider />
                        {Object.values(users).map(({ user }) => {
                            const fullName = getFullName(user);
                            return (
                                <Fragment key={user.id}>
                                    <Accordion
                                        expanded={selectedUser?.id === user.id}
                                        onClick={() => toggleSelectUser(user.id)}
                                        startSlot={
                                            <Avatar src={getAvatarSrc(user.imageId)} title={fullName} size="small" />
                                        }
                                        title={fullName || user.email || ''}
                                        withHeaderBorder={false}
                                        bodyPadding="0"
                                        headerPadding={{ left: '0', right: '0' }}
                                        body={
                                            <UnlockForm
                                                hasUnlockPin={!!selectedUser?.hasUnlockPin}
                                                user={user}
                                                isSaving={isSaving}
                                                setIsSaving={setIsSaving}
                                            />
                                        }
                                    />
                                    <Divider />
                                </Fragment>
                            );
                        })}
                    </Stack>
                </Stack>
                <Button size="medium" onClick={loginWithAnotherAccount} type="submit">
                    <LocalizedText textKey={TextKey.LoginWithAnotherAccount} />
                </Button>
            </Stack>
        </NewPublicFormWrapper>
    );
};
